// src/pages/Reports/TicketRecallByAgentReport/index.tsx
// Mục 14.b, README_UPDATE.md — tách khỏi Reports/index.tsx (trước đây gộp 3 báo cáo vào 1 trang có
// tab), giờ mỗi báo cáo là 1 trang/route riêng. Mục 15.b/15.e — thêm STT, phân trang, và khối tổng
// hợp (tổng số vé giao/thu hồi/doanh thu giao/doanh thu sau thu hồi, tính trên TOÀN BỘ danh sách
// đang lọc chứ không chỉ trang đang xem). Dữ liệu lấy trực tiếp từ ticket-service
// (ReportsController) — chỉ đọc, không có thao tác tạo/sửa/xoá.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardBody, CardHeader, Col, Container, Input, Label, Row, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';

import BreadCrumb from 'Components/Common/BreadCrumb';
import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import AgentPagination from 'pages/AgentManagement/AgentPagination';
import { getIssuancePlans, getTicketRecallByAgentReportApi } from 'helpers/fakebackend_helper';
import { AgentMap, buildAgentMap, getAgentCode, getAgentName, loadAgentsWithCache } from 'helpers/agentLookup';
import { IAgent } from 'slices/agentManagement/types';
import { IssuancePlan } from 'pages/IssuancePlan/types';
import { useBrandingTitle } from '../../../hooks';
import { ReportPagination, TicketRecallByAgentReportRow, TicketRecallByAgentReportSummary } from '../types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('reports_ticket_recall_by_agent_report');

const PAGE_SIZE = 10;

const formatNumber = (value?: number | null) =>
  typeof value === 'number' ? value.toLocaleString('vi-VN') : '0';

const formatCurrency = (value?: number | null) =>
  typeof value === 'number' ? st('value_d_text', { value: value.toLocaleString('vi-VN') }) : '-';

const getPayloadItems = (response: any): any[] => {
  const payload = response?.data || response;
  return payload?.items || payload?.data || payload || [];
};

// Loại bỏ param rỗng/undefined trước khi gọi API — xem README_RULE.md mục 1.
const cleanParams = (params: any): any => {
  const cleaned: any = {};
  Object.keys(params || {}).forEach((key) => {
    const value = params[key];
    if (value !== null && value !== undefined && value !== '') {
      cleaned[key] = value;
    }
  });
  return cleaned;
};

const EMPTY_SUMMARY: TicketRecallByAgentReportSummary = {
  totalDeliveredQuantity: 0,
  totalRecalledQuantity: 0,
  totalDeliveredRevenue: 0,
  totalNetRevenue: 0,
};

const TicketRecallByAgentReport: React.FC = () => {
  useBrandingTitle(st('bao_cao_thu_hoi_ve_e_text'));

  const [plans, setPlans] = useState<IssuancePlan[]>([]);
  const [agents, setAgents] = useState<IAgent[]>([]);
  const agentMap: AgentMap = useMemo(() => buildAgentMap(agents), [agents]);

  const [planId, setPlanId] = useState('');
  const [agentId, setAgentId] = useState('');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<TicketRecallByAgentReportRow[]>([]);
  const [summary, setSummary] = useState<TicketRecallByAgentReportSummary>(EMPTY_SUMMARY);
  const [pagination, setPagination] = useState<ReportPagination>({
    currentPage: 1,
    pageSize: PAGE_SIZE,
    totalItems: 0,
    totalPages: 0,
  });

  useEffect(() => {
    getIssuancePlans({ pageIndex: 1, pageSize: 200 })
      .then((res) => setPlans(getPayloadItems(res) as IssuancePlan[]))
      .catch(() => toast.error(st('khong_the_tai_danh_sach_ke_message')));
    loadAgentsWithCache().then(setAgents);
  }, []);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTicketRecallByAgentReportApi(
        cleanParams({
          PlanId: planId || undefined,
          AgentId: agentId || undefined,
          PageIndex: pagination.currentPage,
          PageSize: pagination.pageSize,
        }),
      );
      const payload = (res as any)?.data ?? res;
      setReport(payload?.page?.items || []);
      setSummary(payload?.summary || EMPTY_SUMMARY);
      setPagination((prev) => ({
        ...prev,
        totalItems: payload?.page?.totalItems || 0,
        totalPages: payload?.page?.totalPages || 0,
      }));
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_tai_bao_cao_message'));
    } finally {
      setLoading(false);
    }
  }, [planId, agentId, pagination.currentPage, pagination.pageSize]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleFilterChange = (setter: (value: string) => void) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setter(event.target.value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={st('bao_cao_thu_hoi_ve_e_text')} pageTitle={st('bao_cao_text')} />

          <Card>
            <CardHeader className="card-header border-0">
              <h5 className="card-title mb-0">{st('bao_cao_thu_hoi_ve_e_text')}</h5>
            </CardHeader>
            <CardBody>
              <Row className="g-3 mb-3">
                <Col md={3}>
                  <div className="border rounded p-2 h-100">
                    <div className="text-muted small">{st('tong_ve_da_giao_text')}</div>
                    <div className="fs-5 fw-semibold">{formatNumber(summary.totalDeliveredQuantity)}</div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="border rounded p-2 h-100">
                    <div className="text-muted small">{st('tong_ve_da_thu_hoi_text')}</div>
                    <div className="fs-5 fw-semibold">{formatNumber(summary.totalRecalledQuantity)}</div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="border rounded p-2 h-100">
                    <div className="text-muted small">{st('tong_doanh_thu_giao_text')}</div>
                    <div className="fs-5 fw-semibold">{formatCurrency(summary.totalDeliveredRevenue)}</div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="border rounded p-2 h-100">
                    <div className="text-muted small">{st('tong_doanh_thu_sau_thu_hoi_text')}</div>
                    <div className="fs-5 fw-semibold text-success">{formatCurrency(summary.totalNetRevenue)}</div>
                  </div>
                </Col>
              </Row>

              <Row className="g-3 mb-4 align-items-end">
                <Col md={4}>
                  <Label className="form-label">{st('ke_hoach_phat_hanh_label')}</Label>
                  <Input type="select" className="form-select" value={planId} onChange={handleFilterChange(setPlanId)}>
                    <option value="">{st('tat_ca_ke_hoach_option')}</option>
                    {plans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.planCode}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={4}>
                  <Label className="form-label">{st('dai_ly_label')}</Label>
                  <Input type="select" className="form-select" value={agentId} onChange={handleFilterChange(setAgentId)}>
                    <option value="">{st('tat_ca_dai_ly_option')}</option>
                    {agents.map((agent) => (
                      <option key={agent.id} value={agent.id}>
                        {agent.code} - {agent.name}
                      </option>
                    ))}
                  </Input>
                </Col>
              </Row>

              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                </div>
              ) : (
                <>
                  <DataTableShell
                    headerRow={
                      <tr>
                        <th className="text-center" style={{ width: 50 }}>{st('stt_column')}</th>
                        <th>{st('dai_ly_label')}</th>
                        <th className="text-end">{st('so_phieu_thu_hoi_column')}</th>
                        <th className="text-end">{st('tong_so_ve_thu_hoi_column')}</th>
                        <th className="text-end">{st('da_duyet_column')}</th>
                        <th className="text-end">{st('tu_choi_column')}</th>
                        <th className="text-end">{st('cho_duyet_column')}</th>
                      </tr>
                    }
                  >
                    {report.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-5">
                          {st('khong_co_du_lieu_text')}
                        </td>
                      </tr>
                    ) : (
                      report.map((row, index) => (
                        <tr key={row.agentId ?? `warehouse-${index}`}>
                          <td className="text-center">{getSttNumber(pagination.currentPage, pagination.pageSize, index)}</td>
                          <td>
                            {row.agentId ? (
                              <>
                                <div className="fw-medium">{getAgentName(agentMap, row.agentId)}</div>
                                <small className="text-muted">{getAgentCode(agentMap, row.agentId)}</small>
                              </>
                            ) : (
                              <span className="text-muted">{st('kho_tong_text')}</span>
                            )}
                          </td>
                          <td className="text-end">{formatNumber(row.recallCount)}</td>
                          <td className="text-end">{formatNumber(row.totalQuantity)}</td>
                          <td className="text-end text-success">{formatNumber(row.approvedQuantity)}</td>
                          <td className="text-end text-danger">{formatNumber(row.rejectedQuantity)}</td>
                          <td className="text-end text-warning">{formatNumber(row.pendingApprovalQuantity)}</td>
                        </tr>
                      ))
                    )}
                  </DataTableShell>

                  <AgentPagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    totalItems={pagination.totalItems}
                    pageSize={pagination.pageSize}
                    onPageChange={(page) => setPagination((prev) => ({ ...prev, currentPage: page }))}
                  />
                </>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TicketRecallByAgentReport;
